import { SearchResponse } from '@algolia/client-search';
import { database } from '@app/firebase';
import ShopperMailingList from '@components/blocks/ShopperMailingList';
import ProductCard from '@components/cards/ProductCard';
import HomesCarousel from '@components/carousels/HomesCarousel';
import FilterControls from '@components/filter-controls/FilterControls';
import Hero from '@components/hero/Hero';
import SearchInput from '@components/search-input/SearchInput';
import UserContext from '@context/UserContext';
import { STYLES } from '@data';
import useFacetSearch, { facetSearchServerSide } from '@hooks/useFacetSearch';
import Sentry from '@integrations/Sentry';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MuiPagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import SortButton from '@ui/components/buttons/SortButton';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { useStyles } from '@ui/utils/makeStyles';
import onSavedClickProduct from '@utils/clickHandlers/onSavedClickProduct';
import { productToSegmentEventData } from '@utils/segmentAnalytics';
import { makeSeoData } from 'config/next-seo.config';
import { collection, getDocs, limit, orderBy, query as firestoreQuery, where } from 'firebase/firestore';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import ErrorPage from '../../_error';

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const homes = await getDocs(firestoreQuery(collection(database, 'homes')
    ,where('published', '==', true)
    ,where('featured', '==', true)
    ,orderBy('install_date', 'desc')
    ,limit(10)));
  const styleObject = STYLES.find(s => s.id === query.id);

  if (!styleObject) {
    return {
      notFound: true,
    };
  }

  const searchResults = await facetSearchServerSide({
    query,
    extraFacetFilters: {
      style: query.id,
    }
  });

  return {
    props: {
      key: query.id,
      query,
      styleObject,
      results: JSON.stringify(searchResults),
      homes: JSON.stringify(homes.docs.map(h => h.data())),
    },
  };
};

function ShopPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter();
  const { theme } = useStyles();
  const userContext = useContext<UserContext>(UserContext);
  const { md } = useBreakpoints();
  const [sortOption, setSortOption] = useState<string>();
  const [anchorEl, setAnchorEl] = useState(null);

  const initResults = useMemo(() => JSON.parse(props.results), [props.results]) as SearchResponse<guesthouse.Product>;
  const homes = useMemo(() => JSON.parse(props.homes), [props.homes]) as guesthouse.Home[];

  const { title, description, image } = useMemo(() => {
    return {
      title: `Shop ${props.styleObject.title} | Home Staging You Can Shop | Guest House`,
      description: `Shop ${props.styleObject.title} products as displayed in our beautifully staged homes. `,
      image: props.styleObject.image,
    };
  }, [props.styleObject]);


  const {
    results,
    filters,
    query,
    setQuery,
    page,
    setPage,
  } = useFacetSearch({
    query: router.query,
    initResults,
    extraFacetFilters: {
      style: props.query.id,
    },
    sortOption
  });

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (Object.keys(router.query).includes('page')) {
      setPage(Number(router.query['page']));
    }
  }, [router.isReady]);

  const handleSortClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };

  if (props.statusCode >= 400) {
    return (<ErrorPage statusCode={props.statusCode} />);
  }

  return (
    <>
      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description, image })} />

      <ContentBlock margin={[0, theme.spacing(3)]}>
        <Hero
          key={props.styleObject?.id}
          backgroundColor="#ededed"
          backgroundImage={props.styleObject.image}
          style={{
            backgroundPosition: props.styleObject.imagePosition ? props.styleObject.imagePosition : '50% 50%',
            color: props.styleObject.imageContrastColor ? props.styleObject.imageContrastColor : theme.palette.common.black,
            textShadow: '0px 0px 20px rgba(0, 0, 0, .7)',
          }}
        >
          <Box textAlign="center">
            <Typography
              variant="display1"
              component="h1"
            >
              {props.styleObject.title}
            </Typography>
          </Box>
        </Hero>
      </ContentBlock>

      <ContentBlock margin={[6, 0, 12]}>
        <Grid
          container
          spacing={4}
        >
          {!md &&
            <Grid
              item
              xs={12}
            >
              <div style={{ width: '100%', marginBottom: md ? 0 : theme.spacing(1.5) }}>
                <SearchInput
                  placeholder="Search for a product"
                  value={query}
                  onValueChange={(value) => {
                    setQuery(value);
                    setPage(0);
                  }}
                />
              </div>
            </Grid>
          }
          <Grid
            item
            xs={6}
            md={12}
            lg={3}
            style={{ paddingTop: 0 }}
          >
            <FilterControls
              filters={[
                filters.category,
                filters.color,
                filters.price
              ]}
              pageReset={() => {
                setPage(0);
              }}
              resultsCount={results.nbHits}
            />
          </Grid>

          {!md &&
            <Grid
              item
              xs={6}
              style={{ paddingTop: 0, paddingLeft: theme.spacing(1.5) }}
            >
              <SortButton
                open={Boolean(anchorEl)}
                handleSortClose={handleSortClose}
                handleSortClick={handleSortClick}
                anchorEl={anchorEl}
                optionSetter={(string) => setSortOption(string)}
                style={{
                  backgroundColor: theme.palette.common.white,
                  padding: theme.spacing(0.5, 1),
                  border: '1px solid #DDDDDD',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              />
            </Grid>
          }

          <Grid
            item
            xs={12}
            lg={9}
            style={{
              paddingTop: md ? theme.spacing(4.5) : 0,
              position: 'relative'
            }}
          >
            {md &&
              <div
                style={{
                  display: 'flex',
                  flexDirection: md ? 'row' : 'column',
                  alignItems: 'center'
                }}
              >
                <Typography
                  style={{
                    marginLeft: theme.spacing(1),
                    marginRight: theme.spacing(4.5),
                  }}
                >
                  {results.nbHits}
                  {' '}
                  {results.nbHits === 1 ? 'ITEM' : 'ITEMS'}
                </Typography>

                <div style={{ width: md ? 300 : '100%', marginBottom: md ? 0 : theme.spacing(1.5) }}>
                  <SearchInput
                    placeholder="Search for a product"
                    value={query}
                    onValueChange={(value) => {
                      setQuery(value);
                      setPage(0);
                    }}
                  />
                </div>
                <SortButton
                  open={Boolean(anchorEl)}
                  handleSortClose={handleSortClose}
                  handleSortClick={handleSortClick}
                  anchorEl={anchorEl}
                  optionSetter={(string) => setSortOption(string)}
                  style={{
                    position: 'absolute',
                    right: 0,
                    backgroundColor: theme.palette.common.white,
                    padding: theme.spacing(0.5, 1),
                    border: '1px solid #DDDDDD',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                />
              </div>
            }

            {
              results.hits?.length
                ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: md ? 'row' : 'column',
                      justifyContent: md ? 'space-between' : 'center',
                      margin: 'auto'
                    }}
                  >
                    {results?.page + 1 === results?.nbPages ?
                      <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                        Showing
                        {' '}
                        {results.page === 0 ?
                          results.nbHits - (results.nbHits - 1)
                          :
                          results.nbHits - (results.nbHits - (results.hitsPerPage * results.page))
                        }
                        {' '}
                        -
                        {' '}
                        {results.nbHits}
                        {' '}
                        of
                        {' '}
                        {results.nbHits}
                      </Typography>
                      :
                      <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                        Showing
                        {' '}
                        {results.hitsPerPage * (results.page + 1) - (results.hitsPerPage - 1)}
                        {' '}
                        -
                        {' '}
                        {results.hitsPerPage * (results.page + 1)}
                        {' '}
                        of
                        {' '}
                        {results.nbHits}
                      </Typography>
                    }
                    {results.nbPages > 1 &&
                      <div>
                        <MuiPagination
                          size={md ? 'medium' : 'small'}
                          count={results.nbPages}
                          page={page === 0 ? 1 : page}
                          style={{ margin: theme.spacing(3) }}
                          onChange={(e, value: number) => {
                            e.preventDefault();
                            setPage(value);
                          }}
                        />
                      </div>
                    }
                  </div>
                ) : (
                  <Typography style={{ margin: theme.spacing(3, 0) }}>
                    No products match your search
                  </Typography>
                )
            }

            <Grid
              container
              spacing={1.5}
            >
              {results?.hits?.length > 0 && results.hits.map((product) => {
                const saved = userContext.savedProducts?.ids.indexOf(product.docID) > -1;

                return (
                  <Grid
                    key={product.docID}
                    item
                    xs={6}
                    md={3}
                  >
                    <ProductCard
                      product={product}
                      saved={saved}
                      onClick={() => {
                        try {
                          window.analytics?.track('Product Clicked', productToSegmentEventData(product));
                        } catch (e) {
                          Sentry.captureException(e);
                        }
                      }}
                      onSavedClick={async (e) => onSavedClickProduct(userContext, saved, product as guesthouse.Product, e)}
                    />
                  </Grid>
                );
              })}
            </Grid>

          </Grid>
        </Grid>

        {results.nbPages > 1 &&
          <div>
            <MuiPagination
              size={md ? 'medium' : 'small'}
              count={results.nbPages}
              page={page === 0 ? 1 : page}
              style={{ margin: theme.spacing(3) }}
              onChange={(e, value: number) => {
                e.preventDefault();
                setPage(value);
              }}
            />
          </div>
        }
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        style={{ backgroundColor: '#f6f6f7', overflowX: 'hidden' }}
        padding={[8, 0]}
        margin={0}
      >
        <ContentBlock
          maxWidth={1365}
          margin={[0, 'auto']}
        >
          <ShopperMailingList
            style={{ marginBottom: theme.spacing(8) }}
          />

          {!!homes?.length && (
            <HomesCarousel
              seeAll
              homes={homes}
            />
          )}
        </ContentBlock>
      </ContentBlock>
    </>
  );
}

export default ShopPage;
